// "Preview message to client": what a Status save sends the firm, shown before it goes. The inbox row is built
// by the same @upm/domain functions the API writes to the outbox, and drawn like the Portal's notifications
// list (FirmWorkInbox). A resolution also shows the request page's banner, which carries the whole note.
import { AtSign, Bell, Check, Clock } from 'lucide-react';
import {
  feedbackEstimateBody, feedbackInboxTitle, feedbackResolvedBody, feedbackUpdateBody, type FeedbackPatchNotice,
} from '@upm/domain';
import { Banner, Button, Dialog, cx } from '../../ui';
import { fmtCalendarDate, type FeedbackStatus, type RequestDetail } from './types';

const ICON = { resolved: Check, estimate: Clock, update: AtSign };
const ICON_TONE = { resolved: 'text-ok-fg', estimate: 'text-brand', update: 'text-ink-2' };

/** The notification's title and body, as the requester's inbox gets them. */
export function inboxText(r: Pick<RequestDetail, 'reference' | 'title'>, notice: FeedbackPatchNotice, next: FeedbackStatus, reason: string) {
  const title = feedbackInboxTitle(r.reference, r.title);
  if (notice.kind === 'resolved') return { title, body: feedbackResolvedBody(notice.status, r.title, reason.trim() || null) };
  if (notice.kind === 'estimate') return { title, body: feedbackEstimateBody(notice.date) };
  return { title, body: feedbackUpdateBody(notice.parts, next) };
}

export function NotifyPreview({ open, request, notice, next, reason, to, busy, onBack, onSend }: {
  open: boolean; request: RequestDetail; notice: FeedbackPatchNotice; next: FeedbackStatus; reason: string; to: string[];
  busy: boolean; onBack: () => void; onSend: () => void;
}) {
  const { title, body } = inboxText(request, notice, next, reason);
  const Icon = ICON[notice.kind];
  const note = reason.trim();
  const today = new Date().toLocaleDateString('en-CA');
  return (
    <Dialog open={open} title="Preview message to client" onClose={onBack} wide
      footer={<>
        <Button onClick={onBack}>Back to edit</Button>
        <Button variant="primary" disabled={busy} onClick={onSend}><Bell aria-hidden size={16} />Send and save</Button>
      </>}>
      <p className="m-0 text-sm">To <span className="font-semibold">{to.join(', ')}</span></p>

      <section aria-label="In their inbox" className="flex flex-col gap-1.5">
        <h3 className="m-0 text-[13px] font-semibold text-muted">In their inbox</h3>
        <div className="overflow-hidden rounded-card border border-line bg-white">
          <div className="border-b border-line px-4 py-2.5 text-sm font-semibold text-ink">Notifications</div>
          <div className="flex items-start gap-2.5 bg-brand-tint px-4 py-2.5">
            <span className={cx('flex h-7 w-7 flex-none items-center justify-center rounded-full bg-subtle', ICON_TONE[notice.kind])}><Icon aria-hidden size={14} /></span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-ink">{title}</div>
              {body && <div className="mt-0.5 text-[13px] text-ink-2">{body}</div>}
              <div className="mt-0.5 text-[11px] text-muted">just now</div>
            </div>
            <span aria-hidden className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-brand" />
          </div>
        </div>
        {notice.kind === 'resolved' && note && body?.endsWith('…') && (
          <p className="m-0 text-[13px] text-muted">The inbox shows the start of your note. They read all of it on their request.</p>
        )}
      </section>

      {notice.kind === 'resolved' && (
        <section aria-label="On their request" className="flex flex-col gap-1.5">
          <h3 className="m-0 text-[13px] font-semibold text-muted">On their request</h3>
          <Banner tone={notice.status === 'done' ? 'success' : 'info'}>
            <div className="font-semibold">{notice.status === 'done' ? `Done ${fmtCalendarDate(today)}` : 'The team won’t do this'}</div>
            <div className="mt-0.5 whitespace-pre-wrap">{note || 'The Vertekal team marked this request done.'}</div>
          </Banner>
        </section>
      )}
    </Dialog>
  );
}
